import Base from "@/components/base";
import {
  useGetCharacteristicsQuery,
  useGetVehicleInfoQuery,
} from "@/generated/graphql";
import { useMemo } from "react";
import Container from "@/components/container";
import { DateTime } from "luxon";
import { getVehicleData } from "@/util/vehicle";

const Vehicle = () => {
  const datetime = useMemo(() => DateTime.now(), []);

  const [{ data: characteristics, fetching: characteristicsFetching }] =
    useGetCharacteristicsQuery({
      variables: {
        timestamp: datetime,
      },
    });
  const [{ data: vehicle, fetching: infoFetching }] = useGetVehicleInfoQuery();

  const isFetching = characteristicsFetching || infoFetching;
  const vehicleInfo = vehicle?.currentEndUser.vehicle;

  const vehicleData = useMemo(() => {
    if (!characteristics || !vehicle?.currentEndUser.vehicle?.model) return {};
    return getVehicleData(
      vehicle.currentEndUser.vehicle.model,
      characteristics,
    );
  }, [characteristics, vehicle]);

  return (
    <Base>
      <Container loading={isFetching}>
        <div className="mt-6 w-full flex flex-grow gap-4 justify-center">
          <PartialCircleProgress
            percentage={vehicleData.stateOfCharge || 0}
            title="State of charge"
          />
          <PartialCircleProgress
            percentage={vehicleData.stateOfHealth || 0}
            title="State of health"
          />
        </div>
        <DetailsContainer>
          <Details
            title="Serial number"
            value={vehicleInfo?.vinNo}
            unit=""
            fetching={infoFetching}
          />
        </DetailsContainer>
        <DetailsContainer>
          <Details
            title="Battery voltage"
            value={vehicleData.batteryVoltage}
            unit="V"
            fetching={characteristicsFetching}
          />
          <Details
            title="Battery temp"
            value={vehicleData.batteryTemperature}
            unit="°C"
            fetching={characteristicsFetching}
          />
          <Details
            title="Cycle count"
            value={vehicleData.cycleCount}
            unit=""
            fetching={characteristicsFetching}
          />
        </DetailsContainer>
        <DetailsContainer>
          <Details
            title="Total mileage"
            value={vehicleData.estimatedDistance}
            unit="km"
            fetching={characteristicsFetching}
          />
          <Details
            title="Today"
            value={vehicleData.estimatedDailyDistance}
            unit="km"
            fetching={characteristicsFetching}
          />
        </DetailsContainer>
        <DetailsContainer>
          <div className="bg-white border border-[#F1FBF4] flex-1 p-2 space-y-2 rounded-md">
            <span className="text-xs text-brand font-medium">
              Vehicle events
            </span>
            <div className="flex items-center justify-between">
              <span className="text-[10px]">No events</span>
            </div>
          </div>
        </DetailsContainer>
      </Container>
    </Base>
  );
};

const Details = ({
  title,
  value,
  unit,
  fetching,
}: {
  title: string;
  value?: string | undefined;
  unit?: string;
  fetching: boolean;
}) => {
  return (
    <div className="bg-white border border-[#F1FBF4] flex-1 p-2 space-y-2 rounded-md min-w-[40%]">
      <p className="text-xs text-brand font-medium">{title} </p>
      {fetching ? (
        <span className="text-[#575757] text-[10px] font-light">
          Loading...
        </span>
      ) : value ? (
        <span className="text-[#575757] text-[10px] font-light">
          {value}
          {unit}
        </span>
      ) : (
        <span className="text-[#575757] text-[10px] font-light">No data</span>
      )}
    </div>
  );
};

const DetailsContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-full flex flex-grow gap-4 mt-8 flex-wrap">{children}</div>
  );
};

const PartialCircleProgress = ({
  percentage,
  title,
}: {
  percentage: number;
  title: string;
}) => {
  // progress
  const strokeDashoffset = (percentage / 100) * 70;

  return (
    <div className="relative size-28">
      <svg
        className="rotate-[144deg] size-full"
        viewBox="0 0 36 36"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-[#C3E2C380]"
          strokeWidth="3"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-white/40"
          strokeWidth="2"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-white/60"
          strokeWidth="1"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>

        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand"
          strokeWidth="2"
          strokeDasharray={`${strokeDashoffset} 100`}
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand-light/50"
          strokeWidth="3"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand"
          strokeWidth="1"
          strokeDasharray={`${strokeDashoffset} 100`}
          strokeLinecap="butt"
        ></circle>
      </svg>

      <div className="absolute w-full top-0 start-1/2 transform -translate-x-1/2 translate-y-[55%] text-center flex flex-col items-center">
        <span className="text-2xl font-bold text-[#4B4B4B]">
          {percentage.toFixed(0)}
          <span className="text-sm font-bold">%</span>
        </span>
        <span className="text-xs tracking-tighter text-[#4B4B4B] mt-5">
          {title}
        </span>
      </div>
    </div>
  );
};

export default Vehicle;
